/**
 * useRefreshTrigger.js
 * Reusable hook for triggering and tracking one or more refresh_triggers rows.
 *
 * Single key:
 *   const { status, requestRefresh } = useRefreshTrigger('purchase_orders');
 *
 * Multiple keys (fires all together, waits for all to complete):
 *   const { status, requestRefresh } = useRefreshTrigger(['purchase_orders', 'stock_counts']);
 *
 * status values:
 *   'idle'    — default, nothing happening
 *   'pending' — requested=true, waiting for poller to pick up all keys
 *   'done'    — all keys cleared (poller ran)
 *   'error'   — supabase error
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const POLL_INTERVAL_MS = 3000;
const DONE_RESET_MS   = 5000;

export function useRefreshTrigger(keys) {
  const keyList = Array.isArray(keys) ? keys : [keys];

  const [status, setStatus]       = useState('idle');
  const [lastRunAt, setLastRunAt] = useState(null);
  const pollRef                   = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const { data, error } = await supabase
        .from('refresh_triggers')
        .select('key, requested, last_run_at')
        .in('key', keyList);

      if (error || !data) return;

      const allDone = keyList.every(k => {
        const row = data.find(r => r.key === k);
        return row && !row.requested;
      });

      if (allDone) {
        stopPolling();
        // Use the most recent last_run_at across all keys
        const latest = data
          .map(r => r.last_run_at)
          .filter(Boolean)
          .sort()
          .pop();
        setLastRunAt(latest);
        setStatus('done');
        setTimeout(() => setStatus('idle'), DONE_RESET_MS);
      }
    }, POLL_INTERVAL_MS);
  }, [keyList.join(',')]);

  const requestRefresh = useCallback(async () => {
    if (status === 'pending') return;
    setStatus('pending');

    const now = new Date().toISOString();
    const updates = keyList.map(k =>
      supabase
        .from('refresh_triggers')
        .update({ requested: true, requested_at: now })
        .eq('key', k)
    );

    const results = await Promise.all(updates);
    const anyError = results.some(r => r.error);

    if (anyError) {
      setStatus('error');
      return;
    }

    startPolling();
  }, [keyList.join(','), status, startPolling]);

  useEffect(() => () => stopPolling(), []);

  return { status, lastRunAt, requestRefresh };
}



/**
 * RefreshButton component
 * Drop-in button that wires up useRefreshTrigger for one or more keys.
 *
 * Props:
 *   triggerKey  — string or string[] matching refresh_triggers.key
 *   label       — button label (default: 'Refresh')
 *   onDone      — optional callback when all refreshes complete
 */
export function RefreshButton({ triggerKey, label = 'Refresh', onDone }) {
  const { status, lastRunAt, requestRefresh } = useRefreshTrigger(triggerKey);

  useEffect(() => {
    if (status === 'done' && onDone) onDone();
  }, [status, onDone]);

  const agoShort = (d) => {
    if (!d) return '';
    const mins = Math.floor((Date.now() - new Date(d)) / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ago`;
  };

  const buttonText = {
    idle:    `↻ ${label}`,
    pending: '⏳ Refreshing…',
    done:    '✓ Done',
    error:   '✕ Error',
  }[status] || `↻ ${label}`;

  const buttonStyle = {
    idle:    'action-btn action-btn--ghost',
    pending: 'action-btn action-btn--ghost',
    done:    'action-btn action-btn--active',
    error:   'action-btn action-btn--close',
  }[status] || 'action-btn action-btn--ghost';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        className={buttonStyle}
        onClick={requestRefresh}
        disabled={status === 'pending'}
        style={{ fontSize: 11, padding: '3px 12px' }}
      >
        {buttonText}
      </button>
      {status === 'done' && lastRunAt && (
        <span style={{ fontSize: 10, color: 'var(--green-light)' }}>
          Updated {agoShort(lastRunAt)}
        </span>
      )}
      {status === 'error' && (
        <span style={{ fontSize: 10, color: '#f87171' }}>
          Could not request refresh
        </span>
      )}
    </div>
  );
}
